/**
 * Nombres de grupo cuando el mismo nombre existe en varias categorías.
 *
 * Eduardo 2026-10-01: «en un torneo puede haber 1 categoría y Grupo 1, pero
 * también en otra categoría puede haber Grupo 1». El nombre ya es único por
 * categoría, no por torneo; donde una pantalla junta grupos de varias
 * categorías (selects, chips, avisos, tabla del jugador) los «Grupo 1»
 * repetidos llevan su categoría: «Grupo 1 (Sub-10)». Si el nombre no se
 * repite, se muestra tal cual (sin ruido para torneos de una categoría).
 */

export interface GroupNameEntry {
  name: string | null | undefined;
  /** Categoría del grupo (id o nombre); null/undefined = sin categoría. */
  divisionKey: number | string | null | undefined;
}

function cleanName(name: string | null | undefined): string {
  return (name ?? "").trim();
}

/** Nombres que aparecen en MÁS de una categoría dentro de la lista. */
export function namesInSeveralDivisions(entries: Iterable<GroupNameEntry>): Set<string> {
  const divisionsByName = new Map<string, Set<string>>();
  for (const entry of entries) {
    const name = cleanName(entry.name);
    if (!name) continue;
    const key = entry.divisionKey === null || entry.divisionKey === undefined ? "none" : String(entry.divisionKey);
    const keys = divisionsByName.get(name) ?? new Set<string>();
    keys.add(key);
    divisionsByName.set(name, keys);
  }
  const repeated = new Set<string>();
  divisionsByName.forEach((keys, name) => {
    if (keys.size > 1) repeated.add(name);
  });
  return repeated;
}

/** «Grupo 1 (Sub-10)» si el nombre está entre los repetidos y hay categoría. */
export function groupNameWithDivision(
  name: string,
  divisionName: string | null | undefined,
  repeatedNames: ReadonlySet<string>,
): string {
  const division = (divisionName ?? "").trim();
  if (!division || !repeatedNames.has(cleanName(name))) return name;
  return `${name} (${division})`;
}

/** Atajo para listas de `TournamentGroup` (traen `division_id` y `division`). */
export function repeatedTournamentGroupNames(
  groups: ReadonlyArray<{ name: string; division_id: number | null; division?: { name: string } | null }>,
): Set<string> {
  return namesInSeveralDivisions(groups.map((group) => ({ name: group.name, divisionKey: group.division_id })));
}

export function tournamentGroupLabel(
  group: { name: string; division?: { name: string } | null },
  repeatedNames: ReadonlySet<string>,
): string {
  return groupNameWithDivision(group.name, group.division?.name, repeatedNames);
}

/** Grupos de la tabla de posiciones (payload del jugador: trae la categoría). */
export interface StandingsGroupNameSource {
  group_name: string;
  division_id?: number | null;
  division_name?: string | null;
}

export function repeatedStandingsGroupNames(groups: ReadonlyArray<StandingsGroupNameSource>): Set<string> {
  return namesInSeveralDivisions(
    groups.map((group) => ({ name: group.group_name, divisionKey: group.division_id ?? group.division_name })),
  );
}

export function standingsGroupLabel(
  group: StandingsGroupNameSource,
  repeatedNames: ReadonlySet<string>,
): string {
  return groupNameWithDivision(group.group_name, group.division_name, repeatedNames);
}
